import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Restaurant } from '@/context/auth/types';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/auth';

interface RestaurantDetails {
  id: string;
  name: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  organisationId?: string;
  organisationName?: string;
  organisationLogoUrl?: string;
  ordering_page_url?: string;
  review_page_url?: string;
  google_review_url?: string;
  facebook_url?: string;
  instagram_url?: string;
}

interface RestaurantContextType {
  selectedRestaurants: string[];
  setSelectedRestaurants: (restaurantIds: string[]) => void;
  syncCooldown: number | null;
  setSyncCooldown: (time: number | null) => void;
  isSyncCoolingDown: boolean;
  timeRemaining: number;
  restaurantDetails: RestaurantDetails[];
  activeRestaurantDetails: RestaurantDetails | null;
  fetchRestaurantDetails: (restaurantIds: string[]) => Promise<void>;
}

const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined);

export const RestaurantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, dataInitialized } = useAuth();
  const [initialized, setInitialized] = useState(false);
  
  // Initialize selected restaurants from localStorage or default to ['all']
  const [selectedRestaurants, setSelectedRestaurantsState] = useState<string[]>(() => {
    const saved = localStorage.getItem('selectedRestaurants');
    return saved ? JSON.parse(saved) : ['all'];
  });

  // Initialize sync cooldown from localStorage
  const [syncCooldown, setSyncCooldownState] = useState<number | null>(() => {
    const saved = localStorage.getItem('syncCooldown');
    return saved ? parseInt(saved, 10) : null;
  });

  // Restaurant details state
  const [restaurantDetails, setRestaurantDetails] = useState<RestaurantDetails[]>([]);
  const [activeRestaurantDetails, setActiveRestaurantDetails] = useState<RestaurantDetails | null>(null);
  
  // Track the last fetched restaurant IDs to prevent redundant fetches
  const [lastFetchedIds, setLastFetchedIds] = useState<string[]>([]);
  
  // Calculate time remaining for cooldown
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isSyncCoolingDown, setIsSyncCoolingDown] = useState<boolean>(false);

  // Update localStorage when selected restaurants change
  const setSelectedRestaurants = (restaurantIds: string[]) => {
    setSelectedRestaurantsState(restaurantIds);
    localStorage.setItem('selectedRestaurants', JSON.stringify(restaurantIds));
  };

  // Update localStorage when sync cooldown changes
  const setSyncCooldown = (time: number | null) => {
    setSyncCooldownState(time);
    if (time) {
      localStorage.setItem('syncCooldown', time.toString());
    } else {
      localStorage.removeItem('syncCooldown');
    }
  };

  // Fetch restaurant details from Supabase - improved memoization and caching
  const fetchRestaurantDetails = useCallback(async (restaurantIds: string[]) => {
    try {
      if (!restaurantIds || !restaurantIds.length || !user?.restaurants) return;
      
      // More robust comparison by sorting the arrays first
      const sortedCurrentIds = [...restaurantIds].sort();
      const sortedLastFetchedIds = lastFetchedIds ? [...lastFetchedIds].sort() : [];
      
      // Skip if we've already fetched these exact IDs (order independent)
      if (JSON.stringify(sortedCurrentIds) === JSON.stringify(sortedLastFetchedIds)) {
        console.log('Skipping redundant restaurant details fetch for same IDs');
        return;
      }
      
      // Update last fetched IDs
      setLastFetchedIds([...restaurantIds]);
      
      // IMPORTANT: Only fetch restaurants that the user has access to
      const userAccessibleRestaurantIds = user.restaurants.map(r => r.id);
      
      // If 'all' is selected, use all of the user's accessible restaurants
      // Otherwise, filter the requested IDs by the accessible ones
      const idsToFetch = restaurantIds.includes('all') 
        ? userAccessibleRestaurantIds 
        : restaurantIds.filter(id => userAccessibleRestaurantIds.includes(id));
      
      // If no valid IDs after filtering, just return
      if (idsToFetch.length === 0) {
        console.warn('No accessible restaurants to fetch after filtering');
        return;
      }
      
      // Modified query to avoid the join with organisations that's causing the error
      const query = supabase.from('restaurants_master').select(`
        restaurant_id,
        restaurant_name,
        logo_url,
        primary_color,
        secondary_color,
        organisation_id,
        ordering_page_url,
        review_page_url,
        google_review_url,
        facebook_url,
        instagram_url
      `).in('restaurant_id', idsToFetch);
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching restaurant details:', error);
        return;
      }
      
      if (data) {
        const formattedDetails: RestaurantDetails[] = data.map(restaurant => ({
          id: restaurant.restaurant_id,
          name: restaurant.restaurant_name,
          logoUrl: restaurant.logo_url,
          primaryColor: restaurant.primary_color,
          secondaryColor: restaurant.secondary_color,
          organisationId: restaurant.organisation_id,
          // Since we've removed the organisations join, we'll set these as undefined
          organisationName: undefined,
          organisationLogoUrl: undefined,
          // URL fields
          ordering_page_url: restaurant.ordering_page_url,
          review_page_url: restaurant.review_page_url,
          google_review_url: restaurant.google_review_url,
          facebook_url: restaurant.facebook_url,
          instagram_url: restaurant.instagram_url
        }));
        
        setRestaurantDetails(formattedDetails);
        
        // Set active restaurant detail to the first selected restaurant
        if (formattedDetails.length > 0) {
          const activeId = selectedRestaurants[0] === 'all' 
            ? formattedDetails[0].id 
            : selectedRestaurants[0];
          
          const activeDetail = formattedDetails.find(r => r.id === activeId) || formattedDetails[0];
          setActiveRestaurantDetails(activeDetail);
        }
      }
    } catch (error) {
      console.error('Unexpected error fetching restaurant details:', error);
    }
  }, [lastFetchedIds, selectedRestaurants, user?.restaurants]);

  // Initialize restaurant data when auth is ready
  useEffect(() => {
    if (user && dataInitialized && !initialized) {
      console.log('Auth data initialized, loading restaurant data');
      
      // If the user has restaurants in their profile, use them to initialize
      if (user.restaurants && user.restaurants.length > 0) {
        // Get the available restaurant IDs from the user profile
        const availableRestaurantIds = user.restaurants.map(r => r.id);

        // If we have saved selected restaurants in localStorage
        const saved = localStorage.getItem('selectedRestaurants');
        if (saved) {
          const savedSelection = JSON.parse(saved);

          // If 'all' is selected, keep it as is
          if (savedSelection.includes('all')) {
            setSelectedRestaurantsState(['all']);
          } else {
            // Only keep saved restaurants that the user still has access to
            const validSelection = savedSelection.filter((id: string) =>
              availableRestaurantIds.includes(id));

            // If no valid selections remain, default to 'all'
            setSelectedRestaurantsState(validSelection.length > 0 ?
              validSelection : ['all']);
          }
        } else {
          // No saved selection, default to 'all'
          setSelectedRestaurantsState(['all']);
        }
      }
      
      // Auto-fetch restaurant details on first load
      fetchRestaurantDetails(selectedRestaurants).then(() => {
        console.log('Initial restaurant details loaded');
        setInitialized(true);
      });
    }
  }, [user, dataInitialized, initialized, selectedRestaurants, fetchRestaurantDetails]);

  // Effect to handle cooldown timer
  useEffect(() => {
    if (!syncCooldown) {
      setIsSyncCoolingDown(false);
      setTimeRemaining(0);
      return;
    }

    const now = Date.now();
    if (syncCooldown > now) {
      setIsSyncCoolingDown(true);
      
      const updateTimeRemaining = () => {
        const currentTime = Date.now();
        const remaining = Math.max(0, syncCooldown - currentTime);
        setTimeRemaining(remaining);
        
        if (remaining <= 0) {
          setSyncCooldown(null);
          setIsSyncCoolingDown(false);
        }
      };
      
      // Initial update
      updateTimeRemaining();
      
      // Set up interval for countdown
      const intervalId = setInterval(updateTimeRemaining, 1000);
      
      return () => clearInterval(intervalId);
    } else {
      // Cooldown has already passed
      setSyncCooldown(null);
      setIsSyncCoolingDown(false);
    }
  }, [syncCooldown]);

  // Update active restaurant when selected restaurants change
  useEffect(() => {
    if (selectedRestaurants.length > 0 && restaurantDetails.length > 0) {
      const activeId = selectedRestaurants[0] === 'all' 
        ? restaurantDetails[0].id 
        : selectedRestaurants[0];
      
      const activeDetail = restaurantDetails.find(r => r.id === activeId) || restaurantDetails[0];
      setActiveRestaurantDetails(activeDetail);
    }
  }, [selectedRestaurants, restaurantDetails]);

  return (
    <RestaurantContext.Provider
      value={{
        selectedRestaurants,
        setSelectedRestaurants,
        syncCooldown,
        setSyncCooldown,
        isSyncCoolingDown,
        timeRemaining,
        restaurantDetails,
        activeRestaurantDetails,
        fetchRestaurantDetails
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
};

export const useRestaurantContext = () => {
  const context = useContext(RestaurantContext);
  if (context === undefined) {
    throw new Error('useRestaurantContext must be used within a RestaurantProvider');
  }
  return context;
};
